/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Link,
  SearchInput,
  Space,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  Tooltip,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { AddIcon, DeleteIcon, FileCopyOIcon } from '@tonic-one/react-icons';
import { useToggle } from '@tonic-one/react-hooks';
import React, { useEffect, useMemo, useState } from 'react';
import iconAlertYellow from 'src/assets/image/icon-alert-yellow.svg';
import applyToAllIcon from 'src/assets/image/icon-apply-all.svg';
import CSpinner from 'src/components/CSpinner';
import customConfig from 'src/config';

import { GlobalApi } from '@Apis';
import ErrorAlert from '../../../../components/Common/ErrorAlert';
import { useDisclosure } from '../../../../components/Common/Tooltip/TextEllipsis/utils';
import IconButton from '../../../../components/IconButton';
import { useUtcOffsetStore } from '../../../../models/useUtcOffsetModel';
import {
  RecipientGroupsProps,
  RecipientGroupType,
} from '../../../../types/globalSetting';
import { reportPendo } from '../../../../utils/setupPendo';
import { getUtcTime } from '../../../../utils/time';
import DelModal from '../../../Report/ActionModal/del';
import SmartTooltip from '../../../Report/SmartTooltip';
import { ApplyConfirmModal } from './ApplyConfirmModal';
import CustomerRecipientGroupModal, {
  createRecipientGroup,
} from './CustomerRecipientGroupModal';
import RecipientGroupsEditModal from './RecipientGroupsEditModal';
import DisabledWarningTip from '../../../../components/Common/Popover/DisabledWarning';
import { useTranslation } from 'react-i18next';
const RecipientGroups = (props: { withEditPermission: boolean }) => {
  const { t } = useTranslation();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [delLoading, setDelLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [delModalTitle, setDelModalTitle] = useState('');
  const [delModalContent, setDelModalContent] = useState<
    JSX.Element | undefined
  >();
  const [loading, setLoading] = useState<boolean>(false);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [isEditModalOpen, toggleEditModalOpen] = useToggle(false);
  const [isApplyModalOpen, toggleApplyModalOpen] = useToggle(false);
  const [selectedRecipientGroup, setSelectedRecipientGroup] =
    useState<RecipientGroupsProps>();
  const [isAddModalOpen, toggleAddModalOpen] = useToggle(false);
  const { offset } = useUtcOffsetStore();
  const [policyRecipientGroups, setPolicyRecipientGroups] = useState<
    RecipientGroupsProps[] | []
  >([]);

  const [allPolicyRecipientGroups, setAllPolicyRecipientGroups] = useState<
    RecipientGroupsProps[] | []
  >([]);

  const [systemRecipientGroups, setSystemRecipientGroups] = useState<
    RecipientGroupsProps[] | []
  >([]);

  const [allRecipientGroups, setAllRecipientGroups] = useState<
    RecipientGroupsProps[] | []
  >([]);
  const searchRecipientGroups = (keyword: string) => {
    if (keyword !== '') {
      const temp = allPolicyRecipientGroups.filter((recipientGroup) => {
        if (
          recipientGroup.isDefault &&
          recipientGroup.type === RecipientGroupType.POLICY
        ) {
          return (
            t(
              'globalSetting.recipientGroup.defaultGroup.policyNotification.name'
            )
              .toLowerCase()
              .indexOf(keyword.toLowerCase()) > -1
          );
        } else {
          return (
            recipientGroup.recipientGroupName
              .toLowerCase()
              .indexOf(keyword.toLowerCase()) > -1
          );
        }
      });
      setPolicyRecipientGroups(temp);
    } else {
      setPolicyRecipientGroups(
        filterDefaultRecipientGroupsByType(
          allRecipientGroups,
          RecipientGroupType.POLICY
        )
      );
    }
  };
  const hoverBackgroundColor = (
    {
      dark: 'rgba(255, 255, 255, 0.12)',
      light: 'rgba(0, 0, 0, 0.12)',
    } as any
  )[colorMode];

  const onEdit = (recipient: RecipientGroupsProps) => {
    setSelectedRecipientGroup(recipient);
    toggleEditModalOpen(true);
  };

  function filterDefaultRecipientGroupsByType(
    data: RecipientGroupsProps[],
    type: RecipientGroupType
  ) {
    return data
      .filter((recipientGroup) => recipientGroup.type === type)
      .sort((a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1));
  }

  async function loadAllData() {
    try {
      setLoading(true);
      const data: RecipientGroupsProps[] = (
        await GlobalApi.getRecipientGroups()
      ).data;
      setPolicyRecipientGroups(
        filterDefaultRecipientGroupsByType(data, RecipientGroupType.POLICY)
      );
      setAllPolicyRecipientGroups(
        filterDefaultRecipientGroupsByType(data, RecipientGroupType.POLICY)
      );
      setSystemRecipientGroups(
        filterDefaultRecipientGroupsByType(data, RecipientGroupType.SYSTEM)
      );
      setAllRecipientGroups(data);
    } catch (error: any) {
      setErrorMsg(t('common.apiException.internalError'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async function doCall() {
      await loadAllData();
    })();
  }, []);

  const onAdd = () => {
    toggleAddModalOpen(true);
  };

  const confirmDel = async () => {
    setDelLoading(true);
    if (!selectedRecipientGroup?.id || selectedRecipientGroup.isDefault) return;
    try {
      const res = await GlobalApi.deleteRecipientGroups(
        selectedRecipientGroup?.id,
        {
          recipientGroupName: selectedRecipientGroup?.recipientGroupName,
          emails: selectedRecipientGroup?.emails,
        }
      );
      if (res.data) {
        window.showNotification({
          titleMsg: t('globalSetting.recipientGroup.delete.success'),
          severity: 'success',
        });
      }
    } catch (error: any) {
      const code = error?.status || 'Unknown';
      if (code === 409) {
        window.showNotification({
          titleMsg: t(
            'globalSetting.recipientGroup.delete.fail.conflict.title'
          ),
          bodyMsg: t(
            'globalSetting.recipientGroup.delete.fail.conflict.content'
          ),
          severity: 'error',
        });
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    } finally {
      reportPendo('GlobalSetting.RecipientGroup.Delete', {
        RecipientGroupId: selectedRecipientGroup?.id,
        RecipientGroupName: selectedRecipientGroup?.recipientGroupName,
      });
      onClose();
      setDelLoading(false);
      await loadAllData();
    }
  };

  const onCope = async (data: RecipientGroupsProps) => {
    const { recipientGroupName, emails, isDefault } = data;
    const name = isDefault
      ? t('globalSetting.recipientGroup.defaultGroup.policyNotification.name')
      : recipientGroupName;
    await createRecipientGroup(`${name} - Copy`, emails.join(','), t);
    await loadAllData();
    reportPendo('GlobalSetting.RecipientGroup.Cope', {
      RecipientGroupId: selectedRecipientGroup?.id,
      RecipientGroupName: selectedRecipientGroup?.recipientGroupName,
    });
  };

  const onApply = async (data: RecipientGroupsProps) => {
    toggleApplyModalOpen(true);
    setSelectedRecipientGroup(data);
  };

  const confirmApply = async () => {
    toggleApplyModalOpen(false);
    if (!selectedRecipientGroup?.id) return;
    try {
      await GlobalApi.applyRecipientGroupToAllPolicies(
        selectedRecipientGroup?.id
      );
      window.showNotification({
        titleMsg: t('globalSetting.recipientGroup.applyToPolicies.success'),
        severity: 'success',
      });
    } catch (error: any) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      reportPendo('GlobalSetting.RecipientGroup.ApplyToAll', {
        RecipientGroupId: selectedRecipientGroup?.id,
        RecipientGroupName: selectedRecipientGroup?.recipientGroupName,
      });
    }
  };

  const onDelete = (data: RecipientGroupsProps) => {
    setDelModalTitle('');
    setDelModalContent(
      <Flex p="1px" pb={0} pr={0} m="23px 0px" alignItems="center">
        <Box mr={12}>
          <Image src={iconAlertYellow} alt="logo" width="12x" height="12x" />
        </Box>
        <Box>
          <Text fontWeight={400} lineHeight="sm">
            {t('globalSetting.recipientGroup.alertModal.delete.content')}
          </Text>
        </Box>
      </Flex>
    );
    setSelectedRecipientGroup(data);
    onOpen();
  };
  const columns: ColumnDef<RecipientGroupsProps>[] = useMemo(
    () => [
      {
        id: 'recipientGroupName',
        header: t('globalSetting.recipientGroup.column.recipientGroupName'),
        cell: ({ row }) => {
          const { recipientGroupName, isDefault, type } = row.original;
          return (
            <Link
              width="100%"
              onClick={() => {
                onEdit(row.original);
              }}
            >
              <SmartTooltip>
                {isDefault
                  ? type === RecipientGroupType.POLICY
                    ? t(
                        'globalSetting.recipientGroup.defaultGroup.policyNotification.name'
                      )
                    : t(
                        'globalSetting.recipientGroup.defaultGroup.systemNotification.name'
                      )
                  : recipientGroupName}
              </SmartTooltip>
            </Link>
          );
        },
        size: 254,
      },
      {
        id: 'recipientsNumber',
        header: t('globalSetting.recipientGroup.column.recipientsNumber'),
        accessorKey: 'recipientNumber',
        size: 100,
        style: {
          textAlign: 'right',
        },
      },
      {
        id: 'emails',
        header: t('globalSetting.recipientGroup.column.emailAddress'),
        cell: ({ row }) => {
          const { emails } = row.original;
          return emails.join(', ');
        },
        // accessorKey: 'emails',
        size: 350,
        style: {
          textAlign: 'right',
        },
      },
      {
        id: 'lastModified',
        header: t('globalSetting.recipientGroup.column.lastModified'),
        cell: ({ row }) => {
          const { lastModified } = row.original;
          return getUtcTime(lastModified, offset, t('common.momentDateFormat'));
        },
        size: 200,
        style: {
          textAlign: 'right',
        },
      },
      {
        id: 'actions',
        header: t('notification.table.fields.actions'),
        cell: ({ row }) => {
          return (
            <Actions
              data={row.original}
              withEditPermission={props.withEditPermission}
            />
          );
        },
        size: 160,
      },
    ],
    [allRecipientGroups]
  );
  const table = useReactTable({
    data: policyRecipientGroups,
    columns,
    defaultColumn: {
      minSize: 40,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  const systemTable = useReactTable({
    data: systemRecipientGroups,
    columns: columns.filter((item) => item.id !== 'actions'),
    defaultColumn: {
      minSize: 40,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });
  const Actions = (props: {
    data: RecipientGroupsProps;
    withEditPermission: boolean;
  }) => {
    return (
      <Stack direction="row" flexWrap="nowrap" spacing="6px">
        <IconButton disabled={!props.withEditPermission}>
          <Tooltip label={t('globalSetting.action.label.applyToAllPolicies')}>
            <Image
              src={applyToAllIcon}
              cursor="pointer"
              onClick={() => onApply(props.data)}
            />
          </Tooltip>
        </IconButton>
        <IconButton
          disabled={
            allRecipientGroups.length >=
              customConfig.ui.globalSettings.limitation
                .recipientGroupMaxNumber || !props.withEditPermission
          }
        >
          <Tooltip label={t('policy.tooltip.copy')}>
            <Icon as={FileCopyOIcon} onClick={() => onCope(props.data)} />
          </Tooltip>
        </IconButton>
        <IconButton disabled={!props.withEditPermission}>
          {!props.data.isDefault && (
            <Tooltip label={t('policy.tooltip.delete')}>
              <Icon as={DeleteIcon} onClick={() => onDelete(props.data)} />
            </Tooltip>
          )}
        </IconButton>
      </Stack>
    );
  };
  return (
    <Box>
      {loading && <CSpinner />}
      <Box>
        {isEditModalOpen && (
          <RecipientGroupsEditModal
            data={selectedRecipientGroup}
            visible={isEditModalOpen}
            loadAllRecipientGroups={loadAllData}
            withEditPermission={props.withEditPermission}
            onClose={() => {
              toggleEditModalOpen(false);
            }}
          />
        )}
        {isAddModalOpen && (
          <CustomerRecipientGroupModal
            visible={isAddModalOpen}
            onClose={() => {
              toggleAddModalOpen(false);
            }}
            loadAllRecipientGroups={loadAllData}
            withEditPermission={props.withEditPermission}
          />
        )}
        {isApplyModalOpen && (
          <ApplyConfirmModal
            data={selectedRecipientGroup}
            onClose={() => {
              toggleApplyModalOpen(false);
            }}
            onConfirm={confirmApply}
            visible={isApplyModalOpen}
          />
        )}
        <DelModal
          visible={isOpen}
          onClose={onClose}
          onConfirm={confirmDel}
          loading={delLoading}
          title={delModalTitle}
          withEditPermission={props.withEditPermission}
        >
          <Text>{delModalContent}</Text>
        </DelModal>
        {errorMsg && (
          <ErrorAlert
            errorMsg={errorMsg}
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <Flex m="16px 0px">
          <Text fontSize="lg" lineHeight="lg" color={colorStyle.color.primary}>
            {t('globalSetting.recipientGroup.policyNotification.title')}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text
            color={colorStyle.color.secondary}
            lineHeight="lg"
            maxWidth={1000}
          >
            {t('globalSetting.recipientGroup.policyNotification.description')}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text>
            <Space>{t('globalSetting.note.title')}</Space>
            <Space width="1x"></Space>
            <Space color={colorStyle.color.secondary}>
              {t('globalSetting.note.applyToAllOrganizations')}
            </Space>
          </Text>
        </Flex>
        <Flex alignItems="center">
          {allRecipientGroups.length >=
            customConfig.ui.globalSettings.limitation.recipientGroupMaxNumber &&
          props.withEditPermission ? (
            <DisabledWarningTip
              popoverTriggerContent={t(
                'globalSetting.recipientGroup.add.limit.warning',
                {
                  count:
                    customConfig.ui.globalSettings.limitation
                      .recipientGroupMaxNumber - 1,
                }
              )}
              popoverTrigger={
                <Button variant="primary" onClick={onAdd} disabled={true}>
                  <Icon as={AddIcon} mr={8} /> {t('common.action.label.add')}
                </Button>
              }
            ></DisabledWarningTip>
          ) : (
            <Button
              variant="primary"
              onClick={onAdd}
              disabled={!props.withEditPermission}
            >
              <Icon as={AddIcon} mr={8} /> {t('common.action.label.add')}
            </Button>
          )}
          <Space width="12px" />
          <SearchInput
            placeholder={t(
              'globalSetting.recipientGroup.defaultGroup.label.groupName.placeholder'
            )}
            onChange={(e: any) => {
              searchRecipientGroups(e.target.value);
            }}
            onClearInput={() => searchRecipientGroups('')}
            width="374px"
          />
        </Flex>
        <Table mt={28}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const styleProps = {
                    minWidth: header.column.columnDef.minSize,
                    width: header.getSize(),
                  };
                  return (
                    <TableHeaderCell key={header.id} {...styleProps}>
                      {header.isPlaceholder ? null : (
                        <SmartTooltip>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </SmartTooltip>
                      )}
                    </TableHeaderCell>
                  );
                })}
              </TableHeaderRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                _hover={{
                  backgroundColor: hoverBackgroundColor,
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const styleProps = {
                    minWidth: cell.column.columnDef.minSize,
                    width: cell.column.getSize(),
                  };
                  return (
                    <TableCell key={cell.id} {...styleProps}>
                      <SmartTooltip>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </SmartTooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Flex m="32px 0px">
          <Text fontSize="lg" lineHeight="lg" color={colorStyle.color.primary}>
            {t('globalSetting.recipientGroup.systemNotification.title')}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text
            color={colorStyle.color.secondary}
            lineHeight="lg"
            maxWidth={1000}
          >
            {t('globalSetting.recipientGroup.systemNotification.description')}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text>
            <Space>{t('globalSetting.note.title')}</Space>
            <Space width="1x"></Space>
            <Space color={colorStyle.color.secondary}>
              {t('globalSetting.note.applyToAllOrganizations')}
            </Space>
          </Text>
        </Flex>
        <Table mt={28}>
          <TableHeader>
            {systemTable.getHeaderGroups().map((headerGroup) => (
              <TableHeaderRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const styleProps = {
                    minWidth: header.column.columnDef.minSize,
                    width: header.getSize(),
                  };
                  return (
                    <TableHeaderCell key={header.id} {...styleProps}>
                      {header.isPlaceholder ? null : (
                        <SmartTooltip>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </SmartTooltip>
                      )}
                    </TableHeaderCell>
                  );
                })}
              </TableHeaderRow>
            ))}
          </TableHeader>
          <TableBody>
            {systemTable.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                _hover={{
                  backgroundColor: hoverBackgroundColor,
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const styleProps = {
                    minWidth: cell.column.columnDef.minSize,
                    width: cell.column.getSize(),
                  };
                  return (
                    <TableCell key={cell.id} {...styleProps}>
                      <SmartTooltip>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </SmartTooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};
export default React.memo(RecipientGroups);
